<script setup lang="ts">
import { ref, reactive, useTemplateRef } from 'vue'
import type { FormSubmitEvent } from '@nuxt/ui'
import type { Organization, OrganizationMember } from '@/stores/admin'
import { useAdmin } from '@/stores/admin'
import { inviteUsersFormSchema } from '@/views/forms/schema.ts'
import type { InviteUsersFormSchema } from '@/views/forms/schema.ts'

const props = defineProps<{
  organization: Organization
}>()

const admin = useAdmin()
// @ts-ignore - auto-imported by unplugin
const toast = useToast()
const members = ref<OrganizationMember[]>([])
const isLoading = ref(true)
const isDeleting = ref<string | null>(null)
const isResending = ref<string | null>(null)
const showInviteForm = ref(false)
const formRef = useTemplateRef('formRef')

const formState = reactive<Partial<InviteUsersFormSchema>>({
  email: undefined,
  name: undefined,
})

async function loadMembers() {
  isLoading.value = true
  try {
    members.value = await admin.fetchOrganizationMembers(props.organization.id)
  } catch (error) {
    console.error('Error loading members:', error)
  } finally {
    isLoading.value = false
  }
}

const inviteError = ref<string | null>(null)

async function onInviteSubmit(event: FormSubmitEvent<InviteUsersFormSchema>) {
  inviteError.value = null
  try {
    await admin.inviteUsers(props.organization.id, [event.data])
    formRef.value?.clear()
    showInviteForm.value = false
    await loadMembers()
  } catch (error) {
    console.error('Error inviting user:', error)
    inviteError.value = error instanceof Error ? error.message : 'Failed to invite user'
  }
}

async function onDeleteMember(member: OrganizationMember) {
  isDeleting.value = member.member_id
  try {
    await admin.deleteOrganizationMember(props.organization.id, member.member_id)
    members.value = members.value.filter((m) => m.member_id !== member.member_id)
  } catch (error) {
    console.error('Error deleting member:', error)
  } finally {
    isDeleting.value = null
  }
}

async function onResendInvite(member: OrganizationMember) {
  isResending.value = member.member_id
  try {
    await admin.resendMemberInvite(props.organization.id, member.member_id)
    toast.add({ title: 'Invite sent', color: 'success' })
    await loadMembers()
  } catch (error) {
    console.error('Error resending invite:', error)
    toast.add({
      title: 'Failed to resend invite',
      description: error instanceof Error ? error.message : undefined,
      color: 'error',
    })
  } finally {
    isResending.value = null
  }
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'active':
      return 'success'
    case 'pending':
      return 'warning'
    case 'invited':
      return 'info'
    case 'expired':
      return 'error'
    default:
      return 'neutral'
  }
}

function formatLastLogin(lastLoginAt: string | null): string {
  if (!lastLoginAt) return 'Never'
  const date = new Date(lastLoginAt)
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

loadMembers()
</script>

<template>
  <div class="space-y-4">
    <div v-if="isLoading" class="grid gap-2">
      <USkeleton class="h-10 w-full" />
      <USkeleton class="h-10 w-full" />
      <USkeleton class="h-10 w-full" />
    </div>

    <div v-else>
      <div v-if="members.length === 0 && !showInviteForm" class="text-center py-8 text-gray-500">
        <UIcon name="i-lucide-users" class="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p>No users in this organization yet.</p>
      </div>

      <ul v-else class="divide-y divide-gray-200 dark:divide-gray-700">
        <li
          v-for="member in members"
          :key="member.member_id"
          class="flex items-center justify-between py-3"
        >
          <div class="flex items-center gap-3">
            <UAvatar :alt="member.name || member.email" size="sm" />
            <div>
              <p class="font-medium">{{ member.name || 'No name' }}</p>
              <p class="text-sm text-gray-500">{{ member.email }}</p>
            </div>
          </div>
          <div class="flex items-center gap-4">
            <div class="text-right text-sm text-gray-500">
              <span class="text-xs">Last login:</span>
              <p>{{ formatLastLogin(member.last_login_at) }}</p>
            </div>
            <UBadge :color="getStatusColor(member.status)" variant="subtle" size="sm">
              {{ member.status }}
            </UBadge>
            <UButton
              v-if="member.status === 'invited' || member.status === 'expired'"
              icon="i-lucide-send"
              color="primary"
              variant="ghost"
              size="sm"
              title="Resend invite email (link expires after 48h)"
              :loading="isResending === member.member_id"
              @click="onResendInvite(member)"
            />
            <UButton
              icon="i-lucide-trash-2"
              color="error"
              variant="ghost"
              size="sm"
              :loading="isDeleting === member.member_id"
              @click="onDeleteMember(member)"
            />
          </div>
        </li>
      </ul>

      <div v-if="showInviteForm" class="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <UAlert
          v-if="inviteError"
          color="error"
          variant="subtle"
          :title="inviteError"
          class="mb-4"
          :close-button="{ icon: 'i-lucide-x', color: 'error', variant: 'link' }"
          @close="inviteError = null"
        />
        <UForm
          ref="formRef"
          :schema="inviteUsersFormSchema"
          :state="formState"
          class="space-y-4"
          @submit="onInviteSubmit"
        >
          <div class="flex gap-2">
            <UFormField name="email" class="flex-1">
              <UInput v-model="formState.email" placeholder="Email" />
            </UFormField>
            <UFormField name="name" class="flex-1">
              <UInput v-model="formState.name" placeholder="Name" />
            </UFormField>
          </div>
          <div class="flex gap-2">
            <UButton type="submit" loading-auto icon="i-lucide-send" size="sm">
              Send invitation
            </UButton>
            <UButton
              type="button"
              variant="ghost"
              color="neutral"
              size="sm"
              @click="showInviteForm = false"
            >
              Cancel
            </UButton>
          </div>
        </UForm>
      </div>

      <UButton
        v-if="!showInviteForm"
        icon="i-lucide-user-plus"
        variant="soft"
        color="primary"
        class="mt-4"
        @click="showInviteForm = true"
      >
        Invite user
      </UButton>
    </div>
  </div>
</template>
